import { Tag } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { FiCalendar } from 'react-icons/fi';
import { VisitScheduleUpdatedNotificationType } from '../../types/notifications';
import { notificationSentAtFormattedDate } from '../../utils';

type ScheduleUpdatedNotificationCardProps = {
  notificationDetails: VisitScheduleUpdatedNotificationType;
  onClick: () => void;
};

/**
 * One planning session's net change for one recipient.
 *
 * `AssignmentBatchNotificationCard` cannot render this: its metadata is one client's visits
 * with a `batchKind`, while a schedule summary spans many clients over many days and carries
 * counts instead. The breakdown into added / removed / retimed is the substance — a bare
 * "6 changes" does not distinguish a shift the caregiver gained from one they lost.
 */
const ScheduleUpdatedNotificationCard = ({ notificationDetails, onClick }: ScheduleUpdatedNotificationCardProps) => {
  const { t } = useTranslation();
  const meta = notificationDetails.metadata?.[0];

  const kindLabel =
    meta?.summaryKind === 'office'
      ? t('notifications-tag-schedule-updated-office')
      : t('notifications-tag-schedule-updated-caregiver');

  // Both bounds or neither, as on the assignment-batch card: a half-rendered span reads as a
  // data error rather than as a missing field.
  const span =
    meta?.firstChangeDate && meta?.lastChangeDate
      ? dayjs(meta.firstChangeDate).isSame(dayjs(meta.lastChangeDate), 'day')
        ? dayjs(meta.firstChangeDate).format('DD MMM YYYY')
        : `${dayjs(meta.firstChangeDate).format('DD MMM YYYY')} — ${dayjs(meta.lastChangeDate).format('DD MMM YYYY')}`
      : null;

  // Zeroes are dropped: "0 removed" is noise, and three pills of which two say nothing bury
  // the one that matters.
  const breakdown = [
    { key: 'notifications-schedule-updated-added', count: meta?.addedCount, tone: 'bg-emerald-50 text-emerald-700' },
    { key: 'notifications-schedule-updated-removed', count: meta?.removedCount, tone: 'bg-rose-50 text-rose-700' },
    { key: 'notifications-schedule-updated-retimed', count: meta?.retimedCount, tone: 'bg-amber-50 text-amber-700' },
  ].filter((item) => typeof item.count === 'number' && item.count > 0);

  return (
    <div
      onClick={onClick}
      className={
        'flex items-start gap-4 bg-white shadow-sm rounded-[10px] p-4 border border-gray-200 hover:shadow-md transition-shadow cursor-pointer'
      }
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-700">
        <FiCalendar size={22} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between">
          <div className="min-w-0 mb-1">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">{notificationDetails.title}</h3>
              <Tag color="geekblue">{kindLabel}</Tag>
            </div>

            <p className="mt-1 text-sm text-gray-700 line-clamp-3">{notificationDetails.message}</p>
          </div>

          <div className="ml-4 flex shrink-0 flex-col items-end gap-4">
            <span
              className={`inline-block h-2.5 w-2.5 rounded-full ${notificationDetails.isRead ? 'bg-gray-300' : 'bg-indigo-600'}`}
            />

            <div className="text-right text-xs text-gray-500">
              {t('notifications-sent-at', { time: notificationSentAtFormattedDate(notificationDetails.sentAt) })}
            </div>
          </div>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          {typeof meta?.changeCount === 'number' && (
            <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
              {t('notifications-schedule-updated-change-count', { count: meta.changeCount })}
            </div>
          )}

          {span && <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">{span}</div>}
        </div>

        {breakdown.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {breakdown.map((item) => (
              <div key={item.key} className={`rounded-full px-3 py-1 text-xs font-medium ${item.tone}`}>
                {t(item.key, { count: item.count })}
              </div>
            ))}
          </div>
        )}

        {meta?.summaryKind === 'office' && typeof meta?.caregiverIds?.length === 'number' && (
          <div className="mt-2 flex gap-1">
            <span className="font-small text-gray-500">
              {t('notifications-schedule-updated-caregivers', { count: meta.caregiverIds.length })}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleUpdatedNotificationCard;
